'use strict';

const libPictView = require('pict-view');

// Where the rail remembers itself. Same prefix as the other per-user client preferences.
const _RAIL_KEY = 'plansheet.notebook.rail';
// Per-device sidebar tab preferences (which tabs are shown, and which is the default on first open).
// Edited in Settings > Appearance; same localStorage convention as plansheet.nav.hidden.
const _TABS_KEY = 'plansheet.sidebar.tabs';

// A small line icon (18x18, inherits currentColor). Keeps the tab catalog readable.
function _svg(pInner) { return '<svg class="ps-nb-tab-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + pInner + '</svg>'; }

// The full set of sidebar tabs, in display order. Key drives the body + the stored prefs; Label is the
// full name (tooltip), Short is the under-icon caption. Navigation is the site map (the primary nav,
// now a tab); Documentation is the Foundational Docs tree. The user turns tabs on/off and picks a
// default in Appearance.
const _TAB_CATALOG =
[
	{ Key: 'notes',         Label: 'Notes',         Short: 'Notes', Icon: _svg('<rect x="4" y="3" width="16" height="18" rx="2"/><line x1="8" y1="8" x2="16" y2="8"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="16" x2="13" y2="16"/>') },
	{ Key: 'bookmarks',     Label: 'Bookmarks',     Short: 'Saved', Icon: _svg('<path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z"/>') },
	{ Key: 'checklists',    Label: 'Checklists',    Short: 'Lists', Icon: _svg('<path d="M10 6h10M10 12h10M10 18h10"/><path d="M3.5 6l1 1 2-2.2M3.5 12l1 1 2-2.2M3.5 18l1 1 2-2.2"/>') },
	{ Key: 'gallery',       Label: 'Gallery',       Short: 'Media', Icon: _svg('<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.6"/><path d="M21 15l-5-5L5 20"/>') },
	{ Key: 'documentation', Label: 'Documentation', Short: 'Docs',  Icon: _svg('<path d="M12 6.5c-1.6-1-4-1.6-5.5-1.6S3 5.2 3 5.5v13c0-.3 1.5-.9 3.5-.9s3.9.6 5.5 1.6m0-13.3c1.6-1 4-1.6 5.5-1.6S21 5.2 21 5.5v13c0-.3-1.5-.9-3.5-.9s-3.9.6-5.5 1.6m0-13.3v13.3"/>') },
	{ Key: 'navigation',    Label: 'Navigation',    Short: 'Nav',   Icon: _svg('<circle cx="12" cy="12" r="9"/><polygon points="16 8 13.2 13.2 8 16 10.8 10.8 16 8"/>') }
];

/**
 * Notebook -- the per-user "notebook by the drafting table", docked on the left of the shell.
 *
 * Three private collections loaded in one read (GET /1.0/Notebook via providers.NotebookAPI):
 *   - Notes      -- markdown, edited with the real pict-section-markdowneditor (the same CodeMirror
 *                   editor the Docs Lake uses), mounted into #PS-Notebook-Note-MDE.
 *   - Bookmarks  -- favorites + recent/trending history.
 *   - Checklists -- the user's personal lists, embedded LIVE via a second 'mine' instance of
 *                   PictView-Checklists (Plansheet-Notebook-Checklists) so items check off in place.
 *
 * Template-driven per the Pict conventions: onBeforeRender derives the render slots into AppData,
 * the templates iterate them with {~TS:~}, and every action mutates AppData + calls render() so the
 * engine repaints through ContentAssignment. The two embedded child views (note editor, checklist
 * collection) own their own DOM; because render() recreates their host divs, they are re-mounted
 * after each panel render (the PictView-DocEditor destroy -> render -> marshalToView pattern).
 * No innerHTML, no document lookups.
 */
class PictViewNotebook extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		// The note editor reads/writes its segments here (ContentDataAddress on the editor instance).
		this.pict.AppData.NotebookNoteEditor = this.pict.AppData.NotebookNoteEditor || { Segments: [] };
		// Which note the live editor is currently showing -- gates the across-repaint edit capture.
		this._editorMountedNoteID = 0;
	}

	_data() { return this.pict.AppData.Notebook || {}; }
	_api() { return this.pict.providers.NotebookAPI; }
	_noteEditor() { return this.pict.views['Plansheet-Notebook-NoteEditor']; }
	_checklistsEmbed() { return this.pict.views['Plansheet-Notebook-Checklists']; }

	_currentRoute()
	{
		let tmpHash = (typeof window !== 'undefined' && window.location && window.location.hash) ? window.location.hash : '';
		return String(tmpHash).replace(/^#/, '') || '/';
	}

	// Parse a server timestamp to epoch ms (best effort).
	_ms(pValue)
	{
		if (!pValue) { return 0; }
		let tmpMs = Date.parse(String(pValue).replace(' ', 'T'));
		return isNaN(tmpMs) ? 0 : tmpMs;
	}

	// A compact "how long ago" label (2h, 3d, 5mo, 1y) for the bookmark/history rows. Short by design so
	// it never forces the row to wrap at narrow / phone width.
	_ago(pValue)
	{
		let tmpMs = this._ms(pValue);
		if (!tmpMs) { return ''; }
		let tmpSec = Math.max(0, Math.floor((Date.now() - tmpMs) / 1000));
		if (tmpSec < 60) { return 'now'; }
		let tmpMin = Math.floor(tmpSec / 60);
		if (tmpMin < 60) { return tmpMin + 'm'; }
		let tmpHr = Math.floor(tmpMin / 60);
		if (tmpHr < 24) { return tmpHr + 'h'; }
		let tmpDay = Math.floor(tmpHr / 24);
		if (tmpDay < 30) { return tmpDay + 'd'; }
		let tmpMon = Math.floor(tmpDay / 30);
		if (tmpMon < 12) { return tmpMon + 'mo'; }
		return Math.floor(tmpMon / 12) + 'y';
	}

	// Derive the template slots from the loaded data + view state into AppData.
	onBeforeRender(pRenderable)
	{
		let tmpD = this._data();
		let tmpSaved = this._restoreRail();
		let tmpEnabledTabs = this._enabledTabs();
		let tmpActive = tmpD.ActiveTab || tmpSaved.ActiveTab || this._defaultTab();
		// A tab turned off in Appearance (or an unknown saved tab) falls back to the default / first enabled.
		if (!tmpEnabledTabs.some((pT) => pT.Key === tmpActive)) { tmpActive = this._defaultTab(); }
		tmpD.ActiveTab = tmpActive;
		if (!tmpD.SelectedNoteID && tmpSaved.SelectedNoteID) { tmpD.SelectedNoteID = tmpSaved.SelectedNoteID; }
		let tmpSelectedID = tmpD.SelectedNoteID || 0;

		// Preserve in-progress edits across this repaint: if the live editor is showing the selected
		// note, pull its current markdown into the segment buffer so the remount restores it. Guarded
		// on _editorMountedNoteID so a note SWITCH (which seeds the new note's segments) is not clobbered.
		if (tmpActive === 'notes' && tmpSelectedID && this._editorMountedNoteID === tmpSelectedID)
		{
			let tmpEd = this._noteEditor();
			if (tmpEd && tmpEd.initialRenderComplete && typeof tmpEd.getAllContent === 'function')
			{
				try { this.pict.AppData.NotebookNoteEditor.Segments = [{ Content: tmpEd.getAllContent('\n\n') }]; } catch (pErr) { /* ignore */ }
			}
		}

		tmpD.Tabs = tmpEnabledTabs.map((pT) => ({ Key: pT.Key, Label: pT.Label, Short: pT.Short, Icon: pT.Icon, Active: tmpActive === pT.Key }));
		// One-or-zero-element slots drive which tab body renders (the pict conditional pattern).
		tmpD.NotesSlot = (tmpActive === 'notes') ? [1] : [];
		tmpD.BookmarksSlot = (tmpActive === 'bookmarks') ? [1] : [];
		tmpD.ChecklistsSlot = (tmpActive === 'checklists') ? [1] : [];
		tmpD.GallerySlot = (tmpActive === 'gallery') ? [1] : [];
		tmpD.DocumentationSlot = (tmpActive === 'documentation') ? [1] : [];
		tmpD.NavigationSlot = (tmpActive === 'navigation') ? [1] : [];

		let tmpNotes = tmpD.Notes || [];
		tmpD.NoteRows = tmpNotes.map((pN) => ({ IDNote: pN.IDNote, Title: pN.Title || 'Untitled note', Active: pN.IDNote === tmpSelectedID }));
		tmpD.NotesEmpty = tmpNotes.length ? [] : [1];
		let tmpSelected = tmpNotes.find((pN) => pN.IDNote === tmpSelectedID);
		// The editor owns the body content (AppData.NotebookNoteEditor.Segments); the slot only carries
		// the id + the title field value.
		tmpD.EditorSlot = tmpSelected
			? [{
				IDNote: tmpSelected.IDNote,
				Title: (tmpD.DraftTitle != null) ? tmpD.DraftTitle : (tmpSelected.Title || '')
			}]
			: [];

		// Favorites carry a subtle age (from CreateDate); the recent-pages history carries when it was last
		// visited (ViewedAt). Both are compact so they never break the row layout at phone width.
		tmpD.FavRows = (tmpD.Favorites || []).map((pB) => ({ IDBookmark: pB.IDBookmark, Label: pB.Label, Route: pB.Route, Age: this._ago(pB.CreateDate) }));
		// Count: number of visits in the trending window. CountLabel is non-empty only when Count > 0
		// so the count span can be hidden with :empty CSS rather than a conditional template slot.
		tmpD.RecentRows = (tmpD.Recent || []).slice(0, 10).map((pR) => ({ Route: pR.Route, Label: pR.Label, Age: this._ago(pR.ViewedAt), CountLabel: pR.Count > 0 ? String(pR.Count) : '' }));
		tmpD.TrendingRows = tmpD.Trending || [];
		tmpD.FavSlot = tmpD.FavRows.length ? [1] : [];
		tmpD.RecentSlot = tmpD.RecentRows.length ? [1] : [];
		tmpD.TrendingSlot = tmpD.TrendingRows.length ? [1] : [];

		// The Checklists tab is the embedded Plansheet-Notebook-Checklists collection; it owns its own
		// rows + empty state, so nothing to derive here beyond the tab slot above.

		// Gallery: the caller's private media (Media.IDUser = them) as thumbnails.
		let tmpMediaAPI = this.pict.providers.MediaAPI;
		tmpD.GalleryRows = (tmpD.Gallery || []).map((pM) => (
		{
			IDMedia: pM.IDMedia,
			Title: pM.Title || pM.OriginalFileName || ('Image ' + pM.IDMedia),
			URL: tmpMediaAPI ? tmpMediaAPI.blobURL(pM.IDMedia) : ('/1.0/Media/' + pM.IDMedia + '/Blob')
		}));
		tmpD.GalleryEmpty = tmpD.GalleryRows.length ? [] : [1];
		// The lightbox is a one-or-zero slot like the tab bodies; _lightbox() carries the full descriptor.
		tmpD.LightboxSlot = tmpD.GalleryLightbox ? [tmpD.GalleryLightbox] : [];

		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		this._mountActiveEmbeds();
		if (!this._LoadedOnce) { this._LoadedOnce = true; this.refresh(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	// Reload from the server, then repaint from data.
	refresh()
	{
		let tmpAPI = this._api();
		if (!tmpAPI || typeof tmpAPI.loadNotebook !== 'function') { return; }
		tmpAPI.loadNotebook().then(() => this.render());
	}

	// Each panel render recreates the embedded controls' host divs, so (re)mount whichever the active
	// tab needs into the fresh host.
	_mountActiveEmbeds()
	{
		let tmpD = this._data();
		let tmpActive = tmpD.ActiveTab || 'notes';
		if (tmpActive === 'notes' && tmpD.SelectedNoteID) { this._mountNoteEditor(); }
		else if (tmpActive === 'checklists') { this._mountChecklistsEmbed(); }
		else if (tmpActive === 'navigation') { this._mountNavEmbed(); }
		else if (tmpActive === 'documentation') { this._mountDocsEmbed(); }
	}

	// The docked site-map navigation (the primary app nav, now a tab) rendered into the Navigation host.
	_mountNavEmbed()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.renderSiteNavInto === 'function') { tmpApp.renderSiteNavInto('#PS-Notebook-Navigation'); }
	}

	// The Foundational Docs tree rendered into the Documentation host. (Tree relocation wired next; the
	// app owns the inline-doc provider wiring so this view stays agnostic about what the tab hosts.)
	_mountDocsEmbed()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.renderDocsTreeInto === 'function') { tmpApp.renderDocsTreeInto('#PS-Notebook-Documentation'); }
		else { this.pict.ContentAssignment.assignContent('#PS-Notebook-Documentation', '<div class="ps-nb-empty">Documentation tree.</div>'); }
	}

	_mountNoteEditor()
	{
		let tmpEd = this._noteEditor();
		if (!tmpEd)
		{
			this.pict.ContentAssignment.assignContent('#PS-Notebook-Note-MDE',
				'<div class="ps-nb-empty">The markdown editor is not available in this build.</div>');
			return;
		}
		try { if (tmpEd.initialRenderComplete) { tmpEd.destroy(); tmpEd.initialRenderComplete = false; } } catch (pErr) { /* ignore */ }
		tmpEd.render();
		tmpEd.marshalToView();
		this._editorMountedNoteID = this._data().SelectedNoteID || 0;
	}

	_mountChecklistsEmbed()
	{
		let tmpEmbed = this._checklistsEmbed();
		if (!tmpEmbed) { return; }
		// show() = reload (fetch the caller's lists + render + mount each checklist control) into the
		// fresh #PS-Notebook-Checklists host.
		try
		{
			if (typeof tmpEmbed.show === 'function') { tmpEmbed.show(); }
			else if (typeof tmpEmbed.render === 'function') { tmpEmbed.render(); }
		}
		catch (pErr) { /* ignore */ }
	}

	/**
	 * The notebook rail is REMEMBERED, not routed.
	 *
	 * The routing audit flagged the rail's tab and note selection as state a reload throws away, and it
	 * is -- but a route is the wrong instrument for it. This rail opens over whatever page you are on,
	 * so giving it a URL would navigate away from that page to show a drawer. The page twin
	 * (PictView-NotesPage) is a page and is correctly routed to /note/:id; this is not.
	 *
	 * So the loss is fixed rather than the mechanism copied: the rail comes back where you left it.
	 */
	_persistRail()
	{
		try
		{
			if (typeof window === 'undefined' || !window.localStorage) { return; }
			let tmpData = this._data();
			window.localStorage.setItem(_RAIL_KEY, JSON.stringify(
				{ ActiveTab: tmpData.ActiveTab || 'notes', SelectedNoteID: tmpData.SelectedNoteID || 0 }));
		}
		catch (pError) { /* storage full or unavailable; the rail is a convenience */ }
	}

	_restoreRail()
	{
		try
		{
			if (typeof window === 'undefined' || !window.localStorage) { return {}; }
			let tmpRaw = window.localStorage.getItem(_RAIL_KEY);
			let tmpSaved = tmpRaw ? JSON.parse(tmpRaw) : {};
			return (tmpSaved && typeof tmpSaved === 'object') ? tmpSaved : {};
		}
		catch (pError) { return {}; }
	}

	// ─── sidebar tab preferences (Settings > Appearance) ────────────────
	// Stored per-device as { Enabled: { key: bool }, Default: key }. A tab is ON unless explicitly off.
	_tabPrefs()
	{
		try
		{
			if (typeof window === 'undefined' || !window.localStorage) { return { Enabled: {}, Default: '' }; }
			let tmpRaw = window.localStorage.getItem(_TABS_KEY);
			let tmpP = tmpRaw ? JSON.parse(tmpRaw) : {};
			return { Enabled: (tmpP && tmpP.Enabled) || {}, Default: (tmpP && tmpP.Default) || '' };
		}
		catch (pErr) { return { Enabled: {}, Default: '' }; }
	}

	// The enabled tabs in catalog order (never empty: if the user somehow turned them all off, show all).
	_enabledTabs()
	{
		let tmpEnabled = this._tabPrefs().Enabled;
		let tmpOn = _TAB_CATALOG.filter((pT) => tmpEnabled[pT.Key] !== false);
		return tmpOn.length ? tmpOn : _TAB_CATALOG.slice();
	}

	// The default tab on first open: the stored Default if it is enabled, else the first enabled tab.
	_defaultTab()
	{
		let tmpOn = this._enabledTabs();
		// Shipped default is Navigation (the primary app nav); the user can repoint it in Appearance.
		let tmpWant = this._tabPrefs().Default || 'navigation';
		return (tmpWant && tmpOn.some((pT) => pT.Key === tmpWant)) ? tmpWant : (tmpOn[0] ? tmpOn[0].Key : 'notes');
	}

	// Called by the Appearance screen after it writes new tab prefs: drop a now-hidden active tab and repaint.
	applyTabPrefs()
	{
		let tmpD = this._data();
		if (!this._enabledTabs().some((pT) => pT.Key === tmpD.ActiveTab)) { tmpD.ActiveTab = this._defaultTab(); }
		this.render();
	}

	// ── public surface for Settings > Appearance ────────────────────────────
	// The full catalog (Key / Label / Short / Icon), in display order, so the Appearance screen can
	// render a row per tab without duplicating the list.
	tabCatalog() { return _TAB_CATALOG.slice(); }

	// The stored prefs ({ Enabled: { key: bool }, Default: key }), normalized. A tab is ON unless off.
	getTabPrefs() { return this._tabPrefs(); }

	// Write new prefs from the Appearance screen, then repaint the rail. We never let the user turn
	// every tab off (an empty rail is a dead end): if the incoming set has none enabled, this keeps the
	// stored prefs untouched and returns false so the caller can snap its control back.
	setTabPrefs(pPrefs)
	{
		let tmpEnabled = (pPrefs && pPrefs.Enabled) || {};
		if (!_TAB_CATALOG.some((pT) => tmpEnabled[pT.Key] !== false)) { return false; }
		let tmpOut = { Enabled: tmpEnabled, Default: (pPrefs && pPrefs.Default) || '' };
		try
		{
			if (typeof window !== 'undefined' && window.localStorage) { window.localStorage.setItem(_TABS_KEY, JSON.stringify(tmpOut)); }
		}
		catch (pErr) { /* best effort; prefs are a per-device convenience */ }
		this.applyTabPrefs();
		return true;
	}

	setTab(pTab)
	{
		this._data().ActiveTab = pTab;
		this._persistRail();
		// The Gallery loads its private media lazily on open (one fetch per tab visit), then repaints.
		if (pTab === 'gallery') { this.refreshGallery(); }
		else { this.render(); }
	}

	// Gallery -- the caller's private media (note images + direct uploads), all Media.IDUser = them.
	refreshGallery()
	{
		let tmpMedia = this.pict.providers.MediaAPI;
		if (!tmpMedia || typeof tmpMedia.listMine !== 'function') { this.render(); return; }
		tmpMedia.listMine()
			.then((pRows) => { this._data().Gallery = Array.isArray(pRows) ? pRows : []; this.render(); })
			.catch(() => this.render());
	}

	// Open the file picker by clicking the hidden input directly. A <label for> over a display:none input
	// is unreliable on some mobile browsers (and blockable by extensions), so drive it explicitly.
	openGalleryPicker()
	{
		let tmpInput = (typeof document !== 'undefined') ? document.getElementById('PS-Notebook-Gallery-Input') : null;
		if (tmpInput) { tmpInput.click(); }
	}

	onGalleryPick(pInput)
	{
		if (!pInput || !pInput.files) { return; }
		this._uploadGalleryFiles(pInput.files);
		pInput.value = '';
	}

	onGalleryDrop(pEvent)
	{
		if (pEvent && typeof pEvent.preventDefault === 'function') { pEvent.preventDefault(); }
		let tmpFiles = (pEvent && pEvent.dataTransfer) ? pEvent.dataTransfer.files : null;
		if (tmpFiles && tmpFiles.length) { this._uploadGalleryFiles(tmpFiles); }
	}

	_uploadGalleryFiles(pFiles)
	{
		let tmpMedia = this.pict.providers.MediaAPI;
		if (!tmpMedia) { return; }
		let tmpImages = [];
		for (let i = 0; i < pFiles.length; i++)
		{
			if (pFiles[i] && pFiles[i].type && pFiles[i].type.indexOf('image/') === 0) { tmpImages.push(pFiles[i]); }
		}
		if (!tmpImages.length) { return; }
		// OwnerType 'User' / IDOwner 0: a direct gallery upload (not tied to a note). Private: true is
		// what makes it the caller's own; the server stamps Media.IDUser from the session.
		Promise.all(tmpImages.map((pF) => tmpMedia.upload('User', 0, pF, { Private: true }).catch(() => null)))
			.then(() => this.refreshGallery());
	}

	copyGalleryRef(pIDMedia)
	{
		let tmpMedia = this.pict.providers.MediaAPI;
		let tmpURL = tmpMedia ? tmpMedia.blobURL(pIDMedia) : ('/1.0/Media/' + pIDMedia + '/Blob');
		let tmpRef = '![](' + tmpURL + ')';
		if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText)
		{
			navigator.clipboard.writeText(tmpRef).then(() => this._toast('Markdown reference copied.', 'success')).catch(() => this._toast(tmpRef, 'info'));
		}
		else { this._toast(tmpRef, 'info'); }
	}

	// The blob URL for a gallery item, from the Media provider (falls back to the canonical route).
	_blobURL(pIDMedia)
	{
		let tmpMedia = this.pict.providers.MediaAPI;
		return tmpMedia ? tmpMedia.blobURL(pIDMedia) : ('/1.0/Media/' + pIDMedia + '/Blob');
	}

	// A human size for the lightbox caption. KB/MB, one decimal, no trailing zero noise.
	_bytes(pSize)
	{
		let tmpSize = Number(pSize) || 0;
		if (tmpSize <= 0) { return ''; }
		if (tmpSize < 1024) { return tmpSize + ' B'; }
		if (tmpSize < 1024 * 1024) { return (Math.round(tmpSize / 102.4) / 10) + ' KB'; }
		return (Math.round(tmpSize / (1024 * 104.8576)) / 10) + ' MB';
	}

	// Filename, size and type, whichever the row carries, for the lightbox subtitle.
	_mediaMeta(pRow)
	{
		let tmpParts = [];
		if (pRow.OriginalFileName) { tmpParts.push(pRow.OriginalFileName); }
		let tmpSize = this._bytes(pRow.FileSize || pRow.Size || pRow.ByteSize || 0);
		if (tmpSize) { tmpParts.push(tmpSize); }
		if (pRow.MimeType) { tmpParts.push(pRow.MimeType); }
		return tmpParts.join('  ·  ');
	}

	// Open the full-size viewer for one image. The lightbox is a one-element AppData slot, so a render
	// paints it; Escape and a backdrop click close it.
	viewGalleryItem(pIDMedia)
	{
		let tmpRow = (this._data().Gallery || []).find((pM) => pM.IDMedia === pIDMedia);
		if (!tmpRow) { return; }
		this._data().GalleryLightbox =
		{
			IDMedia: pIDMedia,
			Title: tmpRow.Title || tmpRow.OriginalFileName || ('Image ' + pIDMedia),
			URL: this._blobURL(pIDMedia),
			Meta: this._mediaMeta(tmpRow)
		};
		this._bindLightboxKeys();
		this.render();
	}

	closeGalleryLightbox()
	{
		this._data().GalleryLightbox = null;
		this._unbindLightboxKeys();
		this.render();
	}

	_bindLightboxKeys()
	{
		if (typeof document === 'undefined' || this._lightboxKeyHandler) { return; }
		this._lightboxKeyHandler = (pEvent) => { if (pEvent.key === 'Escape') { this.closeGalleryLightbox(); } };
		document.addEventListener('keydown', this._lightboxKeyHandler);
	}

	_unbindLightboxKeys()
	{
		if (typeof document === 'undefined' || !this._lightboxKeyHandler) { return; }
		document.removeEventListener('keydown', this._lightboxKeyHandler);
		this._lightboxKeyHandler = null;
	}

	// Save the original bytes. ?download=1 makes the server send Content-Disposition: attachment (and take
	// the proxy path so it works even when blobs are direct-served from object storage), so the real
	// filename lands in Downloads instead of the image opening inline.
	downloadGalleryItem(pIDMedia)
	{
		if (typeof document === 'undefined') { return; }
		let tmpBlobURL = this._blobURL(pIDMedia);
		let tmpURL = tmpBlobURL + (tmpBlobURL.indexOf('?') < 0 ? '?' : '&') + 'download=1';
		let tmpRow = (this._data().Gallery || []).find((pM) => pM.IDMedia === pIDMedia);
		let tmpName = tmpRow ? (tmpRow.OriginalFileName || tmpRow.Title || ('media-' + pIDMedia)) : ('media-' + pIDMedia);
		let tmpAnchor = document.createElement('a');
		tmpAnchor.href = tmpURL;
		tmpAnchor.download = tmpName;
		tmpAnchor.rel = 'noopener';
		document.body.appendChild(tmpAnchor);
		tmpAnchor.click();
		document.body.removeChild(tmpAnchor);
	}

	// View the raw image on its own (inline), handy for zooming with the browser's own controls.
	openGalleryItemTab(pIDMedia)
	{
		if (typeof window !== 'undefined') { window.open(this._blobURL(pIDMedia), '_blank', 'noopener'); }
	}

	deleteGalleryItem(pIDMedia)
	{
		let tmpMedia = this.pict.providers.MediaAPI;
		if (!tmpMedia || typeof tmpMedia.removeMine !== 'function') { return; }
		let tmpLightbox = this._data().GalleryLightbox;
		if (tmpLightbox && tmpLightbox.IDMedia === pIDMedia) { this._data().GalleryLightbox = null; this._unbindLightboxKeys(); }
		tmpMedia.removeMine(pIDMedia).then(() => this.refreshGallery()).catch(() => {});
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMessage, { type: pType || 'info', duration: (pType === 'error') ? 6000 : 2200 });
		}
	}

	selectNote(pID)
	{
		let tmpD = this._data();
		tmpD.SelectedNoteID = (tmpD.SelectedNoteID === pID) ? 0 : pID;
		this._persistRail();
		let tmpNote = (tmpD.Notes || []).find((pN) => pN.IDNote === tmpD.SelectedNoteID);
		tmpD.DraftTitle = tmpNote ? (tmpNote.Title || '') : null;
		// Seed the editor with the selected note's body; the capture guard in onBeforeRender will not
		// overwrite this because _editorMountedNoteID still points at the previously-mounted note.
		this.pict.AppData.NotebookNoteEditor.Segments = tmpNote ? [{ Content: tmpNote.Content || '' }] : [];
		this.render();
	}

	// The title is a plain input marshalled into the draft on the way in (no re-render per keystroke);
	// the body lives in the markdown editor and is read on save.
	onDraftTitle(pValue) { this._data().DraftTitle = pValue; }

	// Images pasted / dragged / picked in a note are owned by that note (OwnerType 'Note', IDOwner =
	// the note's id) so the bytes live in Parime instead of bloating the note as a base64 data URI.
	// The note always has a real id by the time its editor is open (newNote creates server-side first).
	noteImageOwnerContext() { return { OwnerType: 'Note', IDOwner: this._data().SelectedNoteID || 0 }; }

	newNote()
	{
		let tmpAPI = this._api();
		if (!tmpAPI) { return; }
		tmpAPI.createNote({ Title: 'Untitled note', Content: '' }).then((pRec) =>
		{
			let tmpD = this._data();
			if (pRec && pRec.IDNote)
			{
				tmpD.SelectedNoteID = pRec.IDNote;
				tmpD.DraftTitle = 'Untitled note';
				this.pict.AppData.NotebookNoteEditor.Segments = [{ Content: '' }];
			}
			this.refresh();
		}).catch(() => {});
	}

	saveNote()
	{
		let tmpAPI = this._api();
		let tmpD = this._data();
		if (!tmpAPI || !tmpD.SelectedNoteID) { return; }
		let tmpEd = this._noteEditor();
		let tmpContent = (tmpEd && typeof tmpEd.getAllContent === 'function') ? tmpEd.getAllContent('\n\n') : '';
		tmpAPI.updateNote(tmpD.SelectedNoteID, { Title: tmpD.DraftTitle || '', Content: tmpContent })
			.then(() => this.refresh()).catch(() => {});
	}

	deleteNote(pID)
	{
		let tmpAPI = this._api();
		if (!tmpAPI) { return; }
		tmpAPI.deleteNote(pID).then(() =>
		{
			let tmpD = this._data();
			if (tmpD.SelectedNoteID === pID)
			{
				tmpD.SelectedNoteID = 0;
				tmpD.DraftTitle = null;
				this.pict.AppData.NotebookNoteEditor.Segments = [];
				this._editorMountedNoteID = 0;
			}
			this.refresh();
		}).catch(() => {});
	}

	pinPage()
	{
		let tmpAPI = this._api();
		if (!tmpAPI) { return; }
		let tmpRoute = this._currentRoute();
		// Match History/Trending: pin the bare page name, not the "... - Plansheet" browser-tab title.
		let tmpTitle = (typeof document !== 'undefined' && document.title) ? document.title : '';
		let tmpApp = this.pict.PictApplication;
		let tmpLabel = tmpTitle
			? ((tmpApp && typeof tmpApp._pageLabelFromTitle === 'function') ? tmpApp._pageLabelFromTitle(tmpTitle) : tmpTitle)
			: tmpRoute;
		tmpAPI.createBookmark({ Label: tmpLabel, Route: tmpRoute, Favorite: 1 }).then(() => this.refresh()).catch(() => {});
	}

	removeBookmark(pID)
	{
		let tmpAPI = this._api();
		if (!tmpAPI) { return; }
		tmpAPI.deleteBookmark(pID).then(() => this.refresh()).catch(() => {});
	}

	go(pRoute)
	{
		if (typeof window === 'undefined') { return; }
		window.location.hash = '#' + String(pRoute || '/').replace(/^#/, '');
	}
}

module.exports = PictViewNotebook;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-Notebook',
	DefaultRenderable: 'Plansheet-Notebook',
	DefaultDestinationAddress: '#Plansheet-Notebook',
	AutoRender: false,
	CSS: /*css*/`
		.ps-notebook { display: flex; flex-direction: column; height: 100%; min-height: 0;
			background: transparent; color: var(--theme-color-text-primary, #1d2230); }
		.ps-notebook-head { flex: 0 0 auto; display: flex; align-items: center; gap: 8px;
			padding: 12px 14px; border-bottom: 1px solid var(--theme-color-border-default, #e3e6ec); }
		.ps-notebook-title { flex: 1; font-size: 15px; font-weight: 600; letter-spacing: 0.01em; }
		.ps-notebook-close { flex: 0 0 auto; width: 28px; height: 28px; border: none; border-radius: 6px;
			background: transparent; color: var(--theme-color-text-secondary, #5b6376); cursor: pointer;
			font-size: 18px; line-height: 1; display: flex; align-items: center; justify-content: center; }
		.ps-notebook-close:hover { background: var(--theme-color-background-hover, #eef1f6); }
		/* Icon-forward tab row: an icon + a short caption, full label on hover (title). Equal-width cells
		   so 5-6 tabs fit a narrow sidebar without wrapping. */
		.ps-nb-tabs { flex: 0 0 auto; display: flex; gap: 2px; padding: 6px 6px;
			border-bottom: 1px solid var(--theme-color-border-default, #e3e6ec); }
		.ps-nb-tab { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; align-items: center; gap: 3px;
			padding: 6px 2px; border: 0; border-radius: 7px; background: transparent;
			color: var(--theme-color-text-secondary, #5b6376); cursor: pointer; }
		.ps-nb-tab-ico { width: 18px; height: 18px; flex: 0 0 auto; }
		.ps-nb-tab-lbl { font-size: 10px; line-height: 1; letter-spacing: 0.02em; max-width: 100%;
			overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.ps-nb-tab:hover { background: var(--theme-color-background-hover, #eef1f6); color: var(--theme-color-text-primary, #2c3140); }
		.ps-nb-tab.is-active { background: color-mix(in srgb, var(--theme-color-brand-primary, #3b6fd4) 12%, transparent);
			color: var(--theme-color-brand-primary, #3b6fd4); }
		/* The Navigation + Documentation tabs host full-bleed embeds (docked nav / doc tree); drop the
		   tab-body reading padding so they use the full column. */
		.ps-nb-tabbody:has(.ps-nb-nav-host), .ps-nb-tabbody:has(.ps-nb-docs-host) { padding: 0; }
		.ps-nb-nav-host, .ps-nb-docs-host { height: 100%; }
		.ps-nb-tabbody { flex: 1; min-height: 0; overflow-y: auto; padding: 12px 14px 24px; }
		.ps-nb-empty { color: var(--theme-color-text-secondary, #5b6376); font-style: italic; font-size: 13px; margin: 10px 2px; }
		.ps-nb-new { width: 100%; padding: 7px 10px; margin-bottom: 10px; font-size: 13px; cursor: pointer;
			border: 1px dashed var(--theme-color-border-default, #c9d0db); border-radius: 8px;
			background: transparent; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-nb-new:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); color: var(--theme-color-text-primary, #2c3140); }
		.ps-nb-notelist { display: flex; flex-direction: column; gap: 2px; margin-bottom: 10px; }
		.ps-nb-noterow { text-align: left; padding: 6px 8px; font-size: 13px; border: none; border-radius: 6px;
			background: transparent; color: var(--theme-color-text-primary, #2c3140); cursor: pointer; width: 100%;
			white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
		.ps-nb-noterow:hover { background: var(--theme-color-background-hover, #eef1f6); }
		.ps-nb-noterow.is-active { background: var(--theme-color-background-hover, #e7edf8); font-weight: 600; }
		.ps-nb-editor { display: flex; flex-direction: column; gap: 6px; margin-top: 6px;
			border-top: 1px solid var(--theme-color-border-default, #e3e6ec); padding-top: 10px; }
		.ps-nb-noteTitle { padding: 6px 8px; font-size: 13px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 6px; }
		.ps-nb-mde { border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 6px; overflow: hidden;
			background: var(--theme-color-background-panel, #fff); }
		.ps-nb-mde .cm-editor { min-height: 220px; }
		.ps-nb-mde .cm-content { min-height: 220px; }
		.ps-nb-editor-actions { display: flex; gap: 6px; }
		.ps-nb-save { flex: 1; padding: 6px 10px; font-size: 13px; cursor: pointer; border: none; border-radius: 6px;
			background: var(--theme-color-brand-primary, #3b6fd4); color: var(--theme-color-text-on-brand, #fff); }
		.ps-nb-del { padding: 6px 10px; font-size: 13px; cursor: pointer; border: 1px solid var(--theme-color-border-default, #d8dde6);
			border-radius: 6px; background: transparent; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-nb-checklists-host { margin-top: 2px; }
		.ps-nb-gallery-input { display: none; }
		.ps-nb-gallery-upload { display: block; text-align: center; }
		.ps-nb-gallery-zone { border: 1px dashed transparent; border-radius: 8px; padding: 2px; transition: border-color 0.15s ease; }
		.ps-nb-gallery-zone:hover { border-color: var(--theme-color-border-default, #d8dde6); }
		.ps-nb-gallery-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
		.ps-nb-gallery-item { position: relative; aspect-ratio: 1 / 1; border-radius: 8px; overflow: hidden;
			border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-secondary, #f1f3f7); }
		.ps-nb-gallery-thumb { width: 100%; height: 100%; object-fit: cover; display: block; cursor: pointer; }
		.ps-nb-gallery-del { position: absolute; top: 4px; right: 4px; width: 22px; height: 22px; border: none; border-radius: 5px;
			background: rgba(20, 24, 32, 0.55); color: #fff; cursor: pointer; font-size: 15px; line-height: 1;
			display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.15s ease; }
		.ps-nb-gallery-item:hover .ps-nb-gallery-del { opacity: 1; }
		.ps-nb-gallery-del:hover { background: var(--theme-color-status-error, #cc3333); }
		/* Full-size image viewer. Fixed overlay over the whole app; the stage shows a checker so a PNG's
		   transparency reads at a glance (stickers), and the image is never cropped (object-fit: contain). */
		.ps-nb-lightbox { position: fixed; inset: 0; z-index: 4000; display: flex; align-items: center; justify-content: center;
			padding: 24px; box-sizing: border-box; background: rgba(15, 19, 26, 0.72); }
		/* Grid (not flex column): the 1fr middle row gives the stage a DEFINITE height, so the image's
		   max-height: 100% actually resolves -- a flex-derived height does not, and the image overflows. */
		.ps-nb-lightbox-card { display: grid; grid-template-rows: auto minmax(0, 1fr) auto; max-width: min(1100px, 92vw); max-height: 92vh;
			background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-default, #d8dde6);
			border-radius: 10px; overflow: hidden; box-shadow: 0 18px 60px rgba(0, 0, 0, 0.4); }
		.ps-nb-lightbox-bar { display: flex; align-items: center; gap: 12px; padding: 10px 12px;
			border-bottom: 1px solid var(--theme-color-border-light, #eef0f4); }
		.ps-nb-lightbox-meta { display: flex; flex-direction: column; min-width: 0; flex: 1 1 auto; }
		.ps-nb-lightbox-title { font-size: 0.92em; font-weight: 600; color: var(--theme-color-text-primary, #1d2230);
			overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.ps-nb-lightbox-sub { font-size: 0.74em; color: var(--theme-color-text-secondary, #5b6376);
			overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.ps-nb-lightbox-x { flex: 0 0 auto; width: 28px; height: 28px; border: none; border-radius: 6px; background: transparent;
			color: var(--theme-color-text-secondary, #8a93a6); font-size: 20px; line-height: 1; cursor: pointer; }
		.ps-nb-lightbox-x:hover { background: var(--theme-color-background-hover, #e0e5ee); color: var(--theme-color-text-primary, #2c3140); }
		.ps-nb-lightbox-stage { min-height: 0; overflow: hidden; display: flex; align-items: center; justify-content: center; padding: 16px;
			background-color: var(--theme-color-background-secondary, #f5f6f8);
			background-image: linear-gradient(45deg, rgba(0, 0, 0, 0.045) 25%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.045) 75%),
				linear-gradient(45deg, rgba(0, 0, 0, 0.045) 25%, transparent 25%, transparent 75%, rgba(0, 0, 0, 0.045) 75%);
			background-size: 20px 20px; background-position: 0 0, 10px 10px; }
		.ps-nb-lightbox-stage img { max-width: 100%; max-height: 100%; object-fit: contain; border-radius: 2px; box-shadow: 0 1px 6px rgba(0, 0, 0, 0.18); }
		.ps-nb-lightbox-actions { display: flex; flex-wrap: wrap; gap: 8px; padding: 10px 12px;
			border-top: 1px solid var(--theme-color-border-light, #eef0f4); }
		.ps-nb-lightbox-btn { padding: 6px 12px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 6px;
			background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1d2230); font-size: 0.82em; cursor: pointer; }
		.ps-nb-lightbox-btn:hover { background: var(--theme-color-background-hover, #eef1f6); }
		.ps-nb-lightbox-btn-danger:hover { background: var(--theme-color-status-error, #cc3333); color: #fff; border-color: var(--theme-color-status-error, #cc3333); }
		.ps-nb-section { margin: 12px 0; }
		.ps-nb-section-title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em;
			color: var(--theme-color-text-secondary, #8a93a6); margin: 0 2px 4px; }
		.ps-nb-bm { display: flex; align-items: center; gap: 6px; padding: 4px 6px; border-radius: 6px; }
		.ps-nb-bm:hover { background: var(--theme-color-background-hover, #eef1f6); }
		.ps-nb-bmlink { flex: 1; font-size: 13px; cursor: pointer; color: var(--theme-color-brand-primary, #3b6fd4);
			white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
		.ps-nb-bmx { width: 22px; height: 22px; border: none; border-radius: 5px; background: transparent;
			color: var(--theme-color-text-secondary, #8a93a6); cursor: pointer; font-size: 15px; line-height: 1; }
		.ps-nb-bmx:hover { background: var(--theme-color-background-hover, #e0e5ee); color: var(--theme-color-text-primary, #2c3140); }
		.ps-nb-count { font-size: 11px; color: var(--theme-color-text-secondary, #8a93a6); }
		/* History visit-count pill: only visible when the server returned Count > 0.
		   CountLabel is '' for pages not visited within the trending window, so :empty hides the span. */
		.ps-nb-count-recent { min-width: 16px; text-align: center; line-height: 16px; padding: 0 5px;
			border-radius: 8px; background: var(--theme-color-background-hover, #eef1f6); font-weight: 600;
			font-variant-numeric: tabular-nums; white-space: nowrap; }
		.ps-nb-count-recent:empty { display: none; }
		/* Compact bookmark/history age -- fixed, tiny, tabular, and shrink-proof so it never wraps a row. */
		.ps-nb-bm-age { flex: 0 0 auto; font-size: 10px; color: var(--theme-color-text-secondary, #8a93a6); font-variant-numeric: tabular-nums; white-space: nowrap; }
		/* On a phone the rail is the whole screen, so let the note editor use every pixel of it. */
		@media (max-width: 860px)
		{
			.ps-nb-tabbody { padding: 10px 10px 24px; }
			.ps-nb-mde, .ps-nb-editor { width: 100%; }
		}
	`,
	Templates:
	[
		{
			Hash: 'Plansheet-Notebook',
			Template: /*html*/`
<div class="ps-notebook">
	<div class="ps-nb-tabs">{~TS:Notebook-Tab:AppData.Notebook.Tabs~}</div>
	<div class="ps-nb-tabbody">
		{~TS:Notebook-NotesTab:AppData.Notebook.NotesSlot~}
		{~TS:Notebook-BookmarksTab:AppData.Notebook.BookmarksSlot~}
		{~TS:Notebook-ChecklistsTab:AppData.Notebook.ChecklistsSlot~}
		{~TS:Notebook-GalleryTab:AppData.Notebook.GallerySlot~}
		{~TS:Notebook-DocumentationTab:AppData.Notebook.DocumentationSlot~}
		{~TS:Notebook-NavigationTab:AppData.Notebook.NavigationSlot~}
	</div>
	{~TS:Notebook-Lightbox:AppData.Notebook.LightboxSlot~}
</div>`
		},
		{
			Hash: 'Notebook-Tab',
			Template: /*html*/`<button class="ps-nb-tab{~NE:Record.Active^ is-active~}" title="{~D:Record.Label~}" aria-label="{~D:Record.Label~}" onclick="{~P~}.views['Plansheet-Notebook'].setTab('{~D:Record.Key~}')">{~D:Record.Icon~}<span class="ps-nb-tab-lbl">{~D:Record.Short~}</span></button>`
		},
		{
			Hash: 'Notebook-NotesTab',
			Template: /*html*/`
<button class="ps-nb-new" onclick="{~P~}.views['Plansheet-Notebook'].newNote()">+ New note</button>
{~TS:Notebook-NotesEmpty:AppData.Notebook.NotesEmpty~}
<div class="ps-nb-notelist">{~TS:Notebook-NoteRow:AppData.Notebook.NoteRows~}</div>
{~TS:Notebook-Editor:AppData.Notebook.EditorSlot~}`
		},
		{
			Hash: 'Notebook-NotesEmpty',
			Template: /*html*/`<p class="ps-nb-empty">No notes yet. Jot down anything you want to keep by the drafting table.</p>`
		},
		{
			Hash: 'Notebook-NoteRow',
			Template: /*html*/`<button class="ps-nb-noterow{~NE:Record.Active^ is-active~}" onclick="{~P~}.views['Plansheet-Notebook'].selectNote({~D:Record.IDNote~})">{~D:Record.Title~}</button>`
		},
		{
			Hash: 'Notebook-Editor',
			Template: /*html*/`
<div class="ps-nb-editor">
	<input class="ps-nb-noteTitle" value="{~D:Record.Title~}" placeholder="Title" oninput="{~P~}.views['Plansheet-Notebook'].onDraftTitle(this.value)" />
	<div class="ps-nb-mde" id="PS-Notebook-Note-MDE"></div>
	<div class="ps-nb-editor-actions">
		<button class="ps-nb-save" onclick="{~P~}.views['Plansheet-Notebook'].saveNote()">Save</button>
		<button class="ps-nb-del" onclick="{~P~}.views['Plansheet-Notebook'].deleteNote({~D:Record.IDNote~})">Delete</button>
	</div>
</div>`
		},
		{
			Hash: 'Notebook-BookmarksTab',
			Template: /*html*/`
<button class="ps-nb-new" onclick="{~P~}.views['Plansheet-Notebook'].pinPage()">+ Pin this page</button>
{~TS:Notebook-FavSection:AppData.Notebook.FavSlot~}
{~TS:Notebook-RecentSection:AppData.Notebook.RecentSlot~}
{~TS:Notebook-TrendingSection:AppData.Notebook.TrendingSlot~}`
		},
		{
			Hash: 'Notebook-FavSection',
			Template: /*html*/`<div class="ps-nb-section"><div class="ps-nb-section-title">Favorites</div>{~TS:Notebook-FavRow:AppData.Notebook.FavRows~}</div>`
		},
		{
			Hash: 'Notebook-FavRow',
			Template: /*html*/`<div class="ps-nb-bm"><a class="ps-nb-bmlink" onclick="{~P~}.views['Plansheet-Notebook'].go('{~D:Record.Route~}')">{~D:Record.Label~}</a><span class="ps-nb-bm-age" title="Bookmarked">{~D:Record.Age~}</span><button class="ps-nb-bmx" title="Remove" onclick="{~P~}.views['Plansheet-Notebook'].removeBookmark({~D:Record.IDBookmark~})">&times;</button></div>`
		},
		{
			Hash: 'Notebook-RecentSection',
			Template: /*html*/`<div class="ps-nb-section"><div class="ps-nb-section-title">History</div>{~TS:Notebook-LinkRow:AppData.Notebook.RecentRows~}</div>`
		},
		{
			Hash: 'Notebook-TrendingSection',
			Template: /*html*/`<div class="ps-nb-section"><div class="ps-nb-section-title">Trending</div>{~TS:Notebook-TrendingRow:AppData.Notebook.TrendingRows~}</div>`
		},
		{
			Hash: 'Notebook-LinkRow',
			Template: /*html*/`<div class="ps-nb-bm"><a class="ps-nb-bmlink" onclick="{~P~}.views['Plansheet-Notebook'].go('{~D:Record.Route~}')">{~D:Record.Label~}</a><span class="ps-nb-count ps-nb-count-recent" title="Recent visits">{~D:Record.CountLabel~}</span><span class="ps-nb-bm-age" title="Last visited">{~D:Record.Age~}</span></div>`
		},
		{
			Hash: 'Notebook-TrendingRow',
			Template: /*html*/`<div class="ps-nb-bm"><a class="ps-nb-bmlink" onclick="{~P~}.views['Plansheet-Notebook'].go('{~D:Record.Route~}')">{~D:Record.Label~}</a><span class="ps-nb-count">{~D:Record.Count~}</span></div>`
		},
		{
			Hash: 'Notebook-ChecklistsTab',
			Template: /*html*/`<div class="ps-nb-checklists-host" id="PS-Notebook-Checklists"></div>`
		},
		{
			Hash: 'Notebook-GalleryTab',
			Template: /*html*/`
<input type="file" id="PS-Notebook-Gallery-Input" class="ps-nb-gallery-input" accept="image/*" multiple onchange="{~P~}.views['Plansheet-Notebook'].onGalleryPick(this)" />
<button type="button" class="ps-nb-new ps-nb-gallery-upload" onclick="{~P~}.views['Plansheet-Notebook'].openGalleryPicker()">+ Upload image</button>
<div class="ps-nb-gallery-zone" ondragover="event.preventDefault()" ondrop="{~P~}.views['Plansheet-Notebook'].onGalleryDrop(event)">
	{~TS:Notebook-GalleryEmpty:AppData.Notebook.GalleryEmpty~}
	<div class="ps-nb-gallery-grid">{~TS:Notebook-GalleryItem:AppData.Notebook.GalleryRows~}</div>
</div>`
		},
		{
			Hash: 'Notebook-GalleryEmpty',
			Template: /*html*/`<p class="ps-nb-empty">No images yet. Upload here or drag one in; images pasted into a note land here too. They are private to you.</p>`
		},
		{
			Hash: 'Notebook-GalleryItem',
			Template: /*html*/`<div class="ps-nb-gallery-item"><img class="ps-nb-gallery-thumb" src="{~D:Record.URL~}" alt="{~D:Record.Title~}" title="{~D:Record.Title~} — click to view" onclick="{~P~}.views['Plansheet-Notebook'].viewGalleryItem({~D:Record.IDMedia~})" /><button class="ps-nb-gallery-del" title="Delete" onclick="{~P~}.views['Plansheet-Notebook'].deleteGalleryItem({~D:Record.IDMedia~})">&times;</button></div>`
		},
		{
			// The full-size viewer. A one-element slot (AppData.Notebook.LightboxSlot) paints it over the
			// whole notebook; the backdrop and the x close it, as does Escape (bound in viewGalleryItem).
			Hash: 'Notebook-Lightbox',
			Template: /*html*/`
<div class="ps-nb-lightbox" onclick="{~P~}.views['Plansheet-Notebook'].closeGalleryLightbox()">
	<div class="ps-nb-lightbox-card" onclick="event.stopPropagation()">
		<div class="ps-nb-lightbox-bar">
			<div class="ps-nb-lightbox-meta"><span class="ps-nb-lightbox-title">{~D:Record.Title~}</span><span class="ps-nb-lightbox-sub">{~D:Record.Meta~}</span></div>
			<button class="ps-nb-lightbox-x" title="Close" aria-label="Close" onclick="{~P~}.views['Plansheet-Notebook'].closeGalleryLightbox()">&times;</button>
		</div>
		<div class="ps-nb-lightbox-stage"><img src="{~D:Record.URL~}" alt="{~D:Record.Title~}" /></div>
		<div class="ps-nb-lightbox-actions">
			<button class="ps-nb-lightbox-btn" onclick="{~P~}.views['Plansheet-Notebook'].downloadGalleryItem({~D:Record.IDMedia~})">Download</button>
			<button class="ps-nb-lightbox-btn" onclick="{~P~}.views['Plansheet-Notebook'].openGalleryItemTab({~D:Record.IDMedia~})">Open in new tab</button>
			<button class="ps-nb-lightbox-btn" onclick="{~P~}.views['Plansheet-Notebook'].copyGalleryRef({~D:Record.IDMedia~})">Copy markdown</button>
			<button class="ps-nb-lightbox-btn ps-nb-lightbox-btn-danger" onclick="{~P~}.views['Plansheet-Notebook'].deleteGalleryItem({~D:Record.IDMedia~})">Delete</button>
		</div>
	</div>
</div>`
		},
		{
			// The Foundational Docs tree mounts here (the InlineDoc-Nav view, relocated from the reader).
			Hash: 'Notebook-DocumentationTab',
			Template: /*html*/`<div class="ps-nb-docs-host" id="PS-Notebook-Documentation"></div>`
		},
		{
			// The docked site-map navigation mounts here (the primary app nav, now a tab).
			Hash: 'Notebook-NavigationTab',
			Template: /*html*/`<div class="ps-nb-nav-host" id="PS-Notebook-Navigation"></div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'Plansheet-Notebook',
			TemplateHash: 'Plansheet-Notebook',
			ContentDestinationAddress: '#Plansheet-Notebook'
		}
	]
};
